// FlagsStore.js
// Overlays admin-uploaded custom flags (from Manage Countries in the admin panel) on top
// of the static Countrieslist dataset (Constants/CountriesAndCodes.js) used everywhere in
// this app's dial-code / country pickers. Fetches once, caches in memory, and every
// component using useCountryFlags() re-renders when the map arrives.
//
// Usage: const flags = useCountryFlags(); <img src={flags[code] || fallbackFlagcdnUrl} />
import { useEffect, useSyncExternalStore } from "react";
import axios from "axios";
import { MAIN_URL } from "./Urls";

let flagsMap = {};
let hasFetched = false;
let isFetching = false;
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return flagsMap;
}

async function ensureFlagsLoaded() {
  if (hasFetched || isFetching) return;
  isFetching = true;
  try {
    const res = await axios.get(`${MAIN_URL}global/countries/flags`);
    const map = {};
    (res.data || []).forEach((c) => {
      if (c?.code && c?.flag_url) map[String(c.code).toUpperCase()] = c.flag_url;
    });
    flagsMap = map;
    hasFetched = true;
    emit();
  } catch (err) {
    console.error("Failed to load custom country flags:", err);
  } finally {
    isFetching = false;
  }
}

export function useCountryFlags() {
  useEffect(() => {
    ensureFlagsLoaded();
  }, []);
  return useSyncExternalStore(subscribe, getSnapshot);
}

// Non-hook helper for one-off lookups outside render (already-loaded map only).
export function getCachedFlagUrl(code) {
  if (!code) return null;
  return flagsMap[String(code).toUpperCase()] || null;
}
